import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Play, Loader2, CheckCircle2, XCircle, Circle, RotateCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useInvalidateSentinel } from "@/hooks/useSentinelCycle";
import { toast } from "sonner";

type StepStatus = "pending" | "running" | "success" | "error" | "skipped";
interface Step {
  id: string;
  label: string;
  fn: string;
  needs?: "audit_id" | "incident_id" | "alert_id" | "none";
  status: StepStatus;
  message?: string;
}

const INITIAL_STEPS: Step[] = [
  { id: "evaluate", label: "1. Avaliar regras", fn: "sentinel-evaluate", status: "pending" },
  { id: "audit", label: "2. Auditar prompts/skills", fn: "sentinel-audit", status: "pending" },
  { id: "propose", label: "3. Gerar propostas", fn: "sentinel-propose", needs: "audit_id", status: "pending" },
  { id: "forecast", label: "4. Prever volume e custo", fn: "sentinel-forecast", status: "pending" },
  { id: "recommend", label: "5. Inteligência e recomendações", fn: "sentinel-recommend", status: "pending" },
  { id: "escalate", label: "6. Escalar alertas pendentes", fn: "sentinel-escalate", status: "pending" },
  { id: "report", label: "7. Gerar relatório executivo", fn: "sentinel-report", status: "pending" },
];

interface Props {
  orgId?: string;
}

export function SentinelCycleRunner({ orgId }: Props) {
  const [steps, setSteps] = useState<Step[]>(INITIAL_STEPS);
  const [running, setRunning] = useState(false);
  const [liveMessage, setLiveMessage] = useState<string>("");
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const stepRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const invalidate = useInvalidateSentinel();

  const completed = steps.filter((s) => s.status === "success" || s.status === "skipped").length;
  const failed = steps.filter((s) => s.status === "error").length;
  const progress = (completed / steps.length) * 100;

  const updateStep = (id: string, patch: Partial<Step>) =>
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  // Move focus to the active step when it starts running
  useEffect(() => {
    if (!activeStepId) return;
    const node = stepRefs.current[activeStepId];
    if (node) {
      // Defer to next tick so DOM is updated and aria-current is in place
      requestAnimationFrame(() => node.focus({ preventScroll: false }));
    }
  }, [activeStepId]);

  const announce = (msg: string) => setLiveMessage(msg);

  const runCycle = async () => {
    if (!orgId) return;
    setRunning(true);
    setSteps(INITIAL_STEPS.map((s) => ({ ...s, status: "pending" })));
    announce("Ciclo Sentinel iniciado.");

    let lastAuditId: string | undefined;

    for (const step of INITIAL_STEPS) {
      updateStep(step.id, { status: "running" });
      setActiveStepId(step.id);
      announce(`Etapa em execução: ${step.label}.`);
      try {
        const body: Record<string, unknown> = { org_id: orgId };
        if (step.needs === "audit_id") {
          if (!lastAuditId) {
            updateStep(step.id, { status: "skipped", message: "Sem audit_id da etapa anterior" });
            announce(`Etapa pulada: ${step.label}. Sem audit_id da etapa anterior.`);
            continue;
          }
          body.audit_id = lastAuditId;
        }

        const { data, error } = await supabase.functions.invoke(step.fn, { body });
        if (error) throw error;

        // Capture audit_id for "propose"
        if (step.id === "audit") {
          const d = data as { audit_id?: string; results?: Array<{ audit_id?: string }> };
          lastAuditId = d?.audit_id ?? d?.results?.[0]?.audit_id;
        }

        const summary = summarize(step.id, data);
        updateStep(step.id, { status: "success", message: summary });
        announce(`Etapa concluída: ${step.label}. ${summary}.`);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        updateStep(step.id, { status: "error", message });
        announce(`Erro na etapa ${step.label}: ${message}.`);
        // Continue execution — best-effort cycle
      }
    }

    setRunning(false);
    setActiveStepId(null);
    invalidate();
    announce("Ciclo Sentinel concluído.");
    toast.success("Ciclo Sentinel concluído");
  };

  const reset = () => {
    setSteps(INITIAL_STEPS.map((s) => ({ ...s, status: "pending", message: undefined })));
    setActiveStepId(null);
    announce("Estado do ciclo limpo.");
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-sm flex items-center gap-2" id="sentinel-cycle-title">
            <RotateCw className="w-4 h-4 text-primary" aria-hidden="true" />
            Ciclo Completo Sentinel
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={reset} disabled={running}>
              Limpar
            </Button>
            <Button size="sm" onClick={runCycle} disabled={running || !orgId}>
              {running ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden="true" />
              ) : (
                <Play className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
              )}
              {running ? "Executando..." : "Executar Ciclo"}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          7 passos sequenciais: avaliação → auditoria → propostas → previsão → inteligência → escalação → relatório.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Visually hidden, polite live region for screen readers */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {liveMessage}
        </div>
        {/* Assertive region for errors so they interrupt */}
        <div role="alert" aria-live="assertive" className="sr-only">
          {failed > 0 && liveMessage.startsWith("Erro") ? liveMessage : ""}
        </div>

        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{completed}/{steps.length} concluídos</span>
            {failed > 0 && <span className="text-destructive">{failed} com erro</span>}
          </div>
          <Progress
            value={progress}
            className="h-1.5"
            aria-label={`Progresso do ciclo: ${completed} de ${steps.length} etapas concluídas`}
          />
        </div>

        <ol
          className="space-y-1.5"
          aria-labelledby="sentinel-cycle-title"
          aria-busy={running}
        >
          {steps.map((s) => {
            const isActive = s.id === activeStepId;
            return (
              <li
                key={s.id}
                ref={(el) => { stepRefs.current[s.id] = el; }}
                tabIndex={-1}
                aria-current={isActive ? "step" : undefined}
                aria-label={`${s.label}. Status: ${statusLabel(s.status)}${s.message ? `. ${s.message}` : ""}`}
                className={`flex items-start gap-2 p-2 rounded border outline-none transition-colors ${
                  isActive
                    ? "border-primary/60 bg-primary/5 ring-2 ring-primary/30"
                    : "border-border/50 focus-visible:ring-2 focus-visible:ring-primary/40"
                }`}
              >
                <StatusIcon status={s.status} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-foreground">{s.label}</span>
                    <Badge variant="outline" className="text-[10px]">{s.fn}</Badge>
                    <span className="sr-only">Status: {statusLabel(s.status)}</span>
                  </div>
                  {s.message && (
                    <p
                      className={`text-[11px] mt-0.5 ${
                        s.status === "error" ? "text-destructive" : "text-muted-foreground"
                      }`}
                    >
                      {s.message}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

function StatusIcon({ status }: { status: StepStatus }) {
  if (status === "running")
    return <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0 mt-0.5" aria-hidden="true" />;
  if (status === "success")
    return <CheckCircle2 className="w-4 h-4 text-success shrink-0 mt-0.5" aria-hidden="true" />;
  if (status === "error")
    return <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" aria-hidden="true" />;
  if (status === "skipped")
    return <Circle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />;
  return <Circle className="w-4 h-4 text-muted-foreground/40 shrink-0 mt-0.5" aria-hidden="true" />;
}

function statusLabel(status: StepStatus): string {
  switch (status) {
    case "running": return "em execução";
    case "success": return "concluída";
    case "error": return "com erro";
    case "skipped": return "pulada";
    default: return "pendente";
  }
}

function summarize(id: string, data: unknown): string {
  if (!data || typeof data !== "object") return "ok";
  const d = data as Record<string, unknown>;
  if (id === "evaluate") return `${d.evaluated ?? 0} regras, ${d.alerts_created ?? 0} alertas`;
  if (id === "audit") return `${(d.results as unknown[] | undefined)?.length ?? 0} tipos auditados`;
  if (id === "propose") return `${d.proposals_created ?? 0} propostas geradas`;
  if (id === "forecast") return `${d.forecasts_created ?? d.created ?? 0} forecasts`;
  if (id === "recommend") return `${(d.recommendations as unknown[] | undefined)?.length ?? 0} recomendações`;
  if (id === "escalate") return `${d.escalated ?? 0} escalados`;
  if (id === "report") return "relatório gerado";
  return "ok";
}
